import {FluentNumber} from "@fluent/bundle";
import {Localized, useLocalization} from "@fluent/react";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import {
	Box,
	IconButton,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableContainer,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {unwrapResult} from "@reduxjs/toolkit";
import React, {useEffect, useMemo, useRef, useState} from "react";

import Widget from "../analytics/widget/Widget";
import WidgetBody from "../analytics/widget/WidgetBody";
import WidgetBodyOverlay from "../analytics/widget/WidgetBodyOverlay";
import WidgetTitle from "../analytics/widget/WidgetTitle";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {keyProvider} from "../../store/keyProvider";
import type {ProgressRate} from "../../store/services/analyticsService";
import {analyticsService} from "../../store/services/analyticsService";
import useSnackbar from "../../store/ui/useSnackbar";
import selectWidgetSettings from "../../store/widgets/selectWidgetSettings";
import updateWidgetSettings from "../../store/widgets/updateWidgetSettings";
import type {StudentProgressRateChangeWidgetSettings as WidgetSettings} from "../../store/widgets/WidgetSettings";
import StudentName from "../students/StudentName";
import TableCell from "../../utils/tables/Cell";
import type {ColumnDefs} from "../../utils/tables/Head";
import TableHead from "../../utils/tables/Head";
import LoadingErrorState from "../../utils/tables/LoadingErrorState";
import NoSearchResultsState from "../../utils/tables/NoSearchResultsState";
import SortOrder from "../../utils/tables/SortOrder";
import TablePagination from "../../utils/tables/TablePagination";
import usePaginationState from "../../utils/tables/usePaginationState";
import useSorting from "../../utils/tables/useSorting";

const defaultPeriodDuration = 10;

function StudentProgressRateChangeWidget(props: {
	id: string;
	title: React.ReactNode;
	courseId: number;
	userId: number;
}) {
	const [settingsOpen, setSettingsOpen] = useState(false);

	const widgetSettings = useAppSelector((state) =>
		selectWidgetSettings<WidgetSettings>(
			state,
			keyProvider.widgetSettings(props.courseId, "student_progress_rate")
		)
	);

	const {l10n} = useLocalization();

	return (
		<Widget id={props.id}>
			<WidgetTitle
				actions={
					<Box sx={{mt: -1.5, mr: -1.5}}>
						<IconButton
							aria-label={
								settingsOpen
									? l10n.getString(
											"student-progress-rate-change-widget-action-close-settings"
									  )
									: l10n.getString(
											"student-progress-rate-change-widget-action-open-settings"
									  )
							}
							onClick={() => {
								setSettingsOpen((prev) => !prev);
							}}
						>
							{settingsOpen ? <CloseIcon /> : <SettingsIcon />}
						</IconButton>
					</Box>
				}
				subtitle={settingsOpen && props.title}
			>
				{settingsOpen ? (
					<Localized id="widget-settings-title">Settings</Localized>
				) : (
					props.title
				)}
			</WidgetTitle>
			<WidgetBody style={{position: "relative"}}>
				<StudentList
					courseId={props.courseId}
					periodDuration={
						widgetSettings?.recentPeriodDuration ?? defaultPeriodDuration
					}
				/>

				<WidgetBodyOverlay in={settingsOpen}>
					<RateWidgetSettings
						courseId={props.courseId}
						userId={props.userId}
						periodDuration={
							widgetSettings?.recentPeriodDuration || defaultPeriodDuration
						}
					/>
				</WidgetBodyOverlay>
			</WidgetBody>
		</Widget>
	);
}

const columnKeys = [
	"studentName",
	"absoluteChange",
	"percentageChange",
	"recentPeriod",
	"baselinePeriod",
] as const;

const columnDefs: ColumnDefs<typeof columnKeys[number], keyof ProgressRate> = {
	studentName: {
		name: (
			<Localized id="student-progress-rate-change-widget-column-student-name-name">
				Student name
			</Localized>
		),
		width: "40%",
	},
	absoluteChange: {
		name: (
			<Localized id="student-progress-rate-change-widget-column-absolute-change-name">
				Change
			</Localized>
		),
		sortOptions: [{field: "absoluteChange"}],
		align: "right",
	},
	percentageChange: {
		name: (
			<Localized id="student-progress-rate-change-widget-column-percentage-change-name">
				Change %
			</Localized>
		),
		sortOptions: [{field: "percentageChange"}],
		align: "right",
	},
	recentPeriod: {
		name: "",
		align: "right",
	},
	baselinePeriod: {
		name: "",
		align: "right",
	},
};

const numberFormat = {maximumFractionDigits: 2};

function StudentList(props: {courseId: number; periodDuration: number}) {
	const {
		page: progressRatesPage,
		fetchFirstPage,
		fetchRelatedPage,
		pageFetchStatus,
		retryFetching,
	} = usePaginationState<ProgressRate, string>();

	const progressRates = progressRatesPage.content;

	const [ratesPerPage, setRatesPerPage] = useState(10);

	const [sortField, sortOrder, changeOrder] = useSorting<keyof ProgressRate>(
		"absoluteChange"
	);

	const columns = useMemo(
		() =>
			columnKeys.map((k) => {
				let def = columnDefs[k];

				switch (k) {
					case "recentPeriod":
						def = {
							...def,
							name: (
								<Localized
									id="student-progress-rate-change-widget-column-recent-period-name"
									vars={{period: props.periodDuration}}
								>
									Last N days
								</Localized>
							),
						};

						break;
					case "baselinePeriod":
						def = {
							...def,
							name: (
								<Localized
									id="student-progress-rate-change-widget-column-baseline-period-name"
									vars={{period: props.periodDuration}}
								>
									Previous N days
								</Localized>
							),
						};

						break;
				}

				return def;
			}),
		[props.periodDuration]
	);

	const columnNumber = columns.length;

	useEffect(
		() =>
			fetchFirstPage(() =>
				analyticsService.getStudentProgressRateStatistics(
					props.courseId,
					props.periodDuration,
					{
						field: sortField,
						descending: sortOrder === SortOrder.Desc,
					},
					ratesPerPage
				)
			),
		[
			fetchFirstPage,
			props.courseId,
			props.periodDuration,
			ratesPerPage,
			sortField,
			sortOrder,
		]
	);

	return (
		<Stack sx={{width: 1}}>
			<Typography sx={{mb: 2}}>
				<Localized
					id="student-progress-rate-change-widget-help"
					vars={{period: props.periodDuration}}
				>
					Student progress rate is calculated as a mean number of completed
					exercises per day for two consecutive periods of N days. A negative
					difference between the rates for these two periods may indicate that
					the student is struggling with the course content.
				</Localized>
			</Typography>

			<TableContainer>
				<Table>
					<TableHead
						columns={columns}
						sortField={sortField}
						sortOrder={sortOrder}
						onOrderChange={changeOrder}
						loading={pageFetchStatus === "pending"}
					/>

					<TableBody sx={{position: "relative"}}>
						{pageFetchStatus === "failed" && (
							<LoadingErrorState
								description={
									<Localized id="student-progress-rate-change-widget-loading-error-descr">
										Something has gone wrong, and we cannot load statistics
									</Localized>
								}
								columnNumber={columnNumber}
								spanWholeTable
								onReload={retryFetching}
							/>
						)}

						{pageFetchStatus === "succeeded" &&
							progressRatesPage.content.length === 0 && (
								<NoSearchResultsState
									columnNumber={columnNumber}
									title={
										<Localized id="student-progress-rate-change-widget-no-results">
											No data
										</Localized>
									}
									description={
										<Localized id="student-progress-rate-change-widget-no-results-descr">
											Try to adjust period duration in the widget settings
										</Localized>
									}
									spanWholeTable
								/>
							)}

						{pageFetchStatus === "succeeded" &&
							progressRates.map((r) => (
								<ProgressRateRow key={r.name} rate={r} />
							))}

						{Array.from(
							{
								length:
									pageFetchStatus === "succeeded"
										? ratesPerPage - progressRates.length
										: ratesPerPage,
							},
							(_, i) => (
								<ProgressRateRowPlaceholder
									key={i}
									hidden={pageFetchStatus !== "pending"}
								/>
							)
						)}
					</TableBody>
				</Table>

				<TablePagination
					onPageChange={fetchRelatedPage}
					pageSize={ratesPerPage}
					onPageSizeChange={setRatesPerPage}
					first={Boolean(progressRatesPage.request.first)}
					last={Boolean(progressRatesPage.request.last)}
					next={Boolean(progressRatesPage.request.next)}
					previous={Boolean(progressRatesPage.request.previous)}
					label={
						<Localized id="student-progress-rate-change-widget-results-per-page">
							Results per page
						</Localized>
					}
					disabled={pageFetchStatus !== "succeeded"}
					pageSizeOptions={[10, 25, 50]}
				/>
			</TableContainer>
		</Stack>
	);
}

function ProgressRateRow(props: {rate: ProgressRate}) {
	const {rate} = props;

	return (
		<TableRow hover>
			<TableCell component="th" def={columnDefs.studentName}>
				<StudentName
					student={{
						userName: rate.name,
						firstName: rate.firstName,
						lastName: rate.lastName,
					}}
				/>
			</TableCell>

			<TableCell
				def={columnDefs.absoluteChange}
				sx={[rate.absoluteChange < 0 && {color: "red"}]}
			>
				<Localized
					id="student-progress-rate-change-widget-column-absolute-change-value"
					vars={{value: new FluentNumber(rate.absoluteChange, numberFormat)}}
				>{`${rate.absoluteChange}`}</Localized>
			</TableCell>

			<TableCell
				def={columnDefs.percentageChange}
				sx={[rate.percentageChange < 0 && {color: "red"}]}
			>
				<Localized
					id="student-progress-rate-change-widget-column-percentage-change-value"
					vars={{value: new FluentNumber(rate.percentageChange, numberFormat)}}
				>{`${rate.percentageChange}`}</Localized>
			</TableCell>

			<TableCell def={columnDefs.recentPeriod}>
				<Localized
					id="student-progress-rate-change-widget-column-recent-period-value"
					vars={{value: new FluentNumber(rate.recentPeriod, numberFormat)}}
				>{`${rate.recentPeriod}`}</Localized>
			</TableCell>

			<TableCell def={columnDefs.baselinePeriod}>
				<Localized
					id="student-progress-rate-change-widget-column-baseline-period-value"
					vars={{value: new FluentNumber(rate.baselinePeriod, numberFormat)}}
				>{`${rate.baselinePeriod}`}</Localized>
			</TableCell>
		</TableRow>
	);
}

function ProgressRateRowPlaceholder(props: {hidden?: boolean}) {
	return (
		<TableRow
			sx={[
				Boolean(props.hidden) && {
					visibility: "hidden",
					"& > td": {
						borderBottom: "1px solid transparent",
					},
				},
			]}
		>
			<TableCell def={columnDefs.studentName}>
				<StudentName loading student={{userName: ""}} />
			</TableCell>

			<TableCell def={columnDefs.absoluteChange}>
				<Box sx={{display: "flex", justifyContent: "flex-end"}}>
					<Typography variant="inherit">
						<Skeleton width="3ch" />
					</Typography>
				</Box>
			</TableCell>

			<TableCell def={columnDefs.percentageChange}>
				<Box sx={{display: "flex", justifyContent: "flex-end"}}>
					<Typography variant="inherit">
						<Skeleton width="5ch" />
					</Typography>
				</Box>
			</TableCell>

			<TableCell def={columnDefs.recentPeriod}>
				<Box sx={{display: "flex", justifyContent: "flex-end"}}>
					<Typography variant="inherit">
						<Skeleton width="3ch" />
					</Typography>
				</Box>
			</TableCell>

			<TableCell def={columnDefs.baselinePeriod}>
				<Box sx={{display: "flex", justifyContent: "flex-end"}}>
					<Typography variant="inherit">
						<Skeleton width="3ch" />
					</Typography>
				</Box>
			</TableCell>
		</TableRow>
	);
}

function RateWidgetSettings(props: {
	userId: number;
	courseId: number;
	periodDuration: number;
}) {
	const [periodDuration, setPeriodDuration] = useState(props.periodDuration);

	const delay = useRef<ReturnType<typeof setTimeout>>();

	const {l10n} = useLocalization();
	const showSnackbar = useSnackbar();

	const dispatch = useAppDispatch();

	function updateSettings(val: number) {
		setPeriodDuration(val);

		clearInterval(delay.current);

		delay.current = setTimeout(async () => {
			try {
				const res = await dispatch(
					updateWidgetSettings({
						userId: props.userId,
						courseId: props.courseId,
						dashboardName: "follow-up",
						widgetKey: "student_progress_rate",
						settings: {recentPeriodDuration: val},
					})
				);

				unwrapResult(res);
			} catch {
				showSnackbar("error", l10n.getString("error-general"));
			}

			clearInterval(delay.current);
		}, 500);
	}

	return (
		<Stack sx={{width: 0.5}}>
			<TextField
				type="number"
				fullWidth
				label={
					<Localized id="student-progress-rate-change-widget-settings-field-period-duration-label">
						Period duration
					</Localized>
				}
				value={periodDuration}
				onChange={({target}) => {
					const val = parseInt(target.value);
					if (val > 0) {
						updateSettings(val);
					}
				}}
			/>
		</Stack>
	);
}

export default StudentProgressRateChangeWidget;
